/**
 * Sample data for the Engagement Progressing page, extracted directly
 * from the uploaded Engagement_Progressing.xlsx (10 rows, 32 columns) and
 * cross-checked against the uploaded screenshot for the grouped header
 * layout and the per-staff drill-down detail (BOOKED_SSS / APPROVED_TRS
 * sections).
 *
 * Column grouping note: the xlsx's merged group-header cells (row 1) are
 * stored one column to the LEFT of where they visually apply (verified
 * against the screenshot) -- COLUMNS below has already been corrected for
 * this, so `group` on each column reflects the visual grouping, not the
 * raw cell position in the source file.
 */

/** @typedef {{ key: string, group: string|null, label: string, span: number }} ProgressingColumn */

export const PROGRESSING_COLUMNS = /** @type {ProgressingColumn[]} */ ([
  { key: 'no', group: null, label: 'No', span: 1 },
  { key: 'clientName', group: 'Description', label: 'Client name', span: 1 },
  { key: 'clientCode', group: 'Description', label: 'Client Code', span: 1 },
  { key: 'engagementName', group: 'Description', label: 'Parent Engagement name', span: 1 },
  { key: 'engagementCode', group: 'Description', label: 'Parent Engagement code', span: 1 },
  { key: 'period', group: 'Description', label: 'Period', span: 1 },
  { key: 'pendingHours', group: 'For the period ended (Pending Approval TRS)', label: 'Total Hours', span: 1, checkable: 'pending' },
  { key: 'pendingCharges', group: 'For the period ended (Pending Approval TRS)', label: 'Total time charges', span: 1 },
  { key: 'approvedHours', group: 'For the period ended (Approved TRS)', label: 'Total Hours', span: 1, checkable: 'approved' },
  { key: 'approvedCharges', group: 'For the period ended (Approved TRS)', label: 'Total time charges', span: 1 },
  { key: 'bookedHours', group: 'For the period ended (booked staff hours on SSS)', label: 'Total Hours', span: 1, checkable: 'booked' },
  { key: 'bookedCharges', group: 'For the period ended (booked staff hours on SSS)', label: 'Total time charges', span: 1 },
  { key: 'budgetHours', group: 'Approved budget', label: 'Total Hours', span: 1 },
  { key: 'budgetTotal', group: 'Approved budget', label: 'Total budget', span: 1 },
  { key: 'elApprovedFee', group: 'EL Approved', label: 'Fee', span: 1 },
  { key: 'approvedRecoveryRate', group: 'Approved recovery', label: 'Rate', span: 1, format: 'percent' },
  { key: 'pctCompleteSssHours', group: '% of completion (SSS)', label: 'Total Hours', span: 1 },
  { key: 'pctCompleteSssCharges', group: '% of completion (SSS)', label: 'Total time charges', span: 1 },
  { key: 'varianceSssHours', group: 'Unfavorable/(favorable) budget variance (SSS)', label: 'Total Hours', span: 1 },
  { key: 'varianceSssCharges', group: 'Unfavorable/(favorable) budget variance (SSS)', label: 'Total time charges', span: 1 },
  { key: 'estCompleteSssHours', group: 'Estimated time to complete (SSS)', label: 'Total Hours', span: 1 },
  { key: 'estCompleteSssCharges', group: 'Estimated time to complete (SSS)', label: 'Total time charges', span: 1 },
  { key: 'newEstimatedSss', group: 'New estimated (SSS)', label: 'Total Hours', span: 1 },
  { key: 'estAdditionalSss', group: 'Estimated additional (SSS)', label: 'Total time charges', span: 1 },
  { key: 'estimatedSss', group: 'Estimated (SSS)', label: 'recovery rate', span: 1 },
  { key: 'pctCompleteTrsFee', group: '% of completion (TRS)', label: 'fee to meet budget', span: 1 },
  { key: 'pctCompleteTrsDate', group: '% of completion (TRS)', label: 'completion date', span: 1 },
  { key: 'varianceTrsHours1', group: 'Unfavorable/(favorable) budget variance (TRS)', label: 'Total Hours', span: 1 },
  { key: 'varianceTrsCharges1', group: 'Unfavorable/(favorable) budget variance (TRS)', label: 'Total time charges', span: 1 },
  { key: 'varianceTrsHours2', group: 'Unfavorable/(favorable) budget variance (TRS)', label: 'Total Hours', span: 1 },
  { key: 'varianceTrsCharges2', group: 'Unfavorable/(favorable) budget variance (TRS)', label: 'Total time charges', span: 1 },
  { key: 'status', group: null, label: 'Status', span: 1 },
]);

/** One row per key in PROGRESSING_COLUMNS, in the same order. */
const RAW_ROWS = [
  [1, 'JS Land Plc.', 'CC20093', 'JS Land Plc. Annual audit 31 December 2024', 'CC20093-AE01', '2025-01-27 - 2025-02-07', 0, 0, 236, 19040, 537, 39620, 457.5, 32620, 11500, 0.3525, 1.17, 1.21, 80, 7000, 80, 7000, 'NA', 'NA', 'NA', 'NA', 'NA', 457.5, 32620, -222, -13580, 'period-end'],
  [2, 'PCG & Partners Co., Ltd', 'CC20062', 'PCG & Partners Co., Ltd Agreed-upon procedures 1 January -30 June 2024', 'CC20062-AE04', '2024-02-05 - 2024-02-09', 0, 0, 0, 0, 73, 6000, 41, 1072.5, 0, 0, 1.78, 5.59, 32, 4927.5, 32, 4927.5, 'NA', 'NA', 'NA', 'NA', 'NA', 41, 1072.5, -41, -1072.5, 'period-end'],
  [3, 'PricewaterHouseCoopers Cambodia Limited.', 'CC00264', 'PricewaterHouseCoopers Cambodia Limited. Annual audit 30 June 2024', 'CC00264-AE04', '2024-07-22 - 2024-09-13', 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 0, 0, 'period-end'],
  [4, 'Pestech (Cambodia) Limited', 'CC10242', 'Pestech (Cambodia) Plc. Annual audit 30 June 2024', 'CC10242-AE08', '2024-06-28 - 2024-11-15', 72, 3240, 242, 20520, 1064, 58640, 0, 0, 0, 0, 0, 0, 0, 0, 1064, 58640, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 242, 20520, 'period-end'],
  [5, 'Cambodia Base Childcare for Garment Factory Worker Project', 'CC00059', 'Cambodia Base Childcare for Garment Factory Worker (CBCC Project)-2023', 'CC00059-AE04', '2024-06-03 - 2024-06-12', 0, 0, 77, 15380, 184, 16880, 0, 0, 0, 0, 0, 0, 0, 0, 184, 16880, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 77, 15380, 'period-end'],
  [6, 'Exceed Worldwide', 'CC20017', 'Exceed Worldwide Annual audit 31 December 2023', 'CC20017-AE04', '2024-05-20 - 2024-06-07', 0, 0, 82, 9860, 160, 9000, 0, 0, 0, 0, 0, 0, 0, 0, 160, 9000, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 82, 9860, 'period-end'],
  [7, 'Viet trans Link Forwarding (Cambodia) Co., Ltd', 'CC00336', 'Viet Trans Link Forwarding (Cambodia) Co., Ltd Annual aduit 31 Dec 2023', 'null', '2023-12-06 - 2024-05-31', 0, 0, 26, 6500, 352, 33440, 0, 0, 0, 0, 0, 0, 0, 0, 352, 33440, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 26, 6500, 'period-end'],
  [8, 'Marist Solidarity Cambodia', 'CC10189', 'Completion- Marist Solidarity Cambodia 31 December 2023', 'CC10189-AE05', '2024-05-13 - 2024-05-17', 0, 0, 0, 0, 40, 6800, 0, 0, 0, 0, 0, 0, 0, 0, 40, 6800, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 0, 0, 'period-end'],
  [9, 'Phum Baitang (Cambodia) Co., Ltd', 'CC30019', 'Completion- Phum Baitang 31 December 2023', 'CC30019-AE04', '2024-05-13 - 2024-05-17', 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 0, 0, 'period-end'],
  [10, 'Nissey (Cambodia) Co., Ltd', 'CC10217', 'Nissey (Cambodia) Co., Ltd Annual audit 31 December 2023', 'CC10217-AE07', '2024-05-13 - 2024-05-31', 0, 0, 72, 12880, 568, 41160, 0, 0, 0, 0, 0, 0, 0, 0, 568, 41160, 'NA', 'NA', 'NA', 'NA', 'NA', 0, 0, 72, 12880, 'period-end'],
];

export const sampleEngagementProgressingRows = RAW_ROWS.map((values, i) => {
  const row = { id: `row-${i + 1}` };
  PROGRESSING_COLUMNS.forEach((col, idx) => {
    row[col.key] = values[idx];
  });
  return row;
});

/**
 * Filter-only fields (Status/Division filters), not part of the xlsx's
 * own 32 columns -- best-effort plausible values per row, keyed by row
 * index, so filtering works in sample-fallback mode too.
 */
const SAMPLE_FILTER_ATTRS = [
  { division: 'Audit', engagementStatus: 'started' },
  { division: 'Advisory', engagementStatus: 'started' },
  { division: 'Audit', engagementStatus: 'started' },
  { division: 'Audit', engagementStatus: 'started' },
  { division: 'Advisory', engagementStatus: 'started' },
  { division: 'Audit', engagementStatus: 'started' },
  { division: 'Audit', engagementStatus: 'completed' },
  { division: 'Audit', engagementStatus: 'completed' },
  { division: 'Audit', engagementStatus: 'completed' },
  { division: 'Audit', engagementStatus: 'started' },
];
sampleEngagementProgressingRows.forEach((row, i) => {
  Object.assign(row, SAMPLE_FILTER_ATTRS[i] ?? { division: null, engagementStatus: null });
});

/**
 * @typedef {object} StaffDetailRow
 * @property {string} name
 * @property {number} rate
 * @property {number} hours
 * @property {number} charges
 * @property {string} status
 */

/**
 * Per-staff drill-down detail, keyed `${rowId}:${category}` where category
 * is 'pending' | 'approved' | 'booked'. Row 1 (JS Land Plc.) uses the
 * EXACT figures from the uploaded screenshot for booked/approved (they
 * reconcile precisely to the main table's totals: 537 hrs / $38,820 for
 * booked, 236 hrs / $19,040 for approved). NOTE: the source screenshot's
 * own main table cell shows $39,620 (not $38,820) for row 1's booked
 * charges -- that ~$800 gap exists in the uploaded source itself, not
 * introduced here; reproduced faithfully rather than "corrected". Every
 * other row's detail is
 * synthesized (see synthesizeStaffDetail below) to reconcile with that
 * row's own known totals, since no real per-staff breakdown exists for
 * them in the source file -- clearly a plausible sample, not real data.
 */
export const STAFF_DETAIL = {
  'row-1:booked': [
    { name: 'AZ Yeezent Ng', rate: 400, hours: 23, charges: 9200, status: 'Chargable' },
    { name: 'AXM Ketetikta Sourn', rate: 170, hours: 26, charges: 4420, status: 'Chargable' },
    { name: 'AASup Chandara Rath', rate: 100, hours: 88, charges: 8800, status: 'Chargable' },
    { name: 'ASenior 1 Sokngim Huot', rate: 60, hours: 80, charges: 4800, status: 'Chargable' },
    { name: 'ASExp Vichara Huot', rate: 45, hours: 80, charges: 3600, status: 'Chargable' },
    { name: 'ATINT Ratana Sann', rate: 20, hours: 80, charges: 1600, status: 'Chargable' },
    { name: 'ASenior 1 Sotavy Mao', rate: 60, hours: 80, charges: 4800, status: 'Chargable' },
    { name: 'ATINT Sodana Hornkongkea', rate: 20, hours: 80, charges: 1600, status: 'Chargable' },
  ],
  'row-1:approved': [
    { name: 'Chandara Rath', rate: 100, hours: 24, charges: 2400, status: 'approved' },
    { name: 'Ketetikta Sourn', rate: 250, hours: 8, charges: 2000, status: 'approved' },
    { name: 'Yee Zent Ng', rate: 400, hours: 16, charges: 6400, status: 'approved' },
    { name: 'Sokngim Huot', rate: 60, hours: 72, charges: 4320, status: 'approved' },
    { name: 'Sotavy Mao', rate: 60, hours: 40, charges: 2400, status: 'approved' },
    { name: 'Ratana Sann', rate: 20, hours: 40, charges: 800, status: 'approved' },
    { name: 'Sodana Hornkongkea', rate: 20, hours: 36, charges: 720, status: 'approved' },
  ],
};

const SAMPLE_STAFF_POOL = [
  { name: 'Sokha Chan', rate: 400 },
  { name: 'Dara Pich', rate: 250 },
  { name: 'Vanna Sok', rate: 100 },
  { name: 'Ratana Kim', rate: 60 },
  { name: 'Sopheak Ly', rate: 20 },
];

/**
 * Deterministically splits a row's known (totalHours, totalCharges) for
 * one category across 2-3 staff from the sample pool, so every non-zero
 * cell in the main table has a working drill-down -- not real data, just
 * a plausible-looking breakdown that reconciles exactly to the totals
 * already shown.
 * @param {number} totalHours
 * @param {number} totalCharges
 * @param {number} seed
 * @returns {StaffDetailRow[]}
 */
function synthesizeStaffDetail(totalHours, totalCharges, seed) {
  if (!totalHours) return [];
  const staffCount = 2 + (seed % 2);
  const staff = Array.from({ length: staffCount }, (_, i) => SAMPLE_STAFF_POOL[(seed + i) % SAMPLE_STAFF_POOL.length]);

  const shares = staff.map((_, i) => (i === staffCount - 1 ? 1 : 0.4 - i * 0.1));
  const shareSum = shares.reduce((a, b) => a + b, 0);

  let hoursUsed = 0;
  const rows = staff.map((s, i) => {
    const isLast = i === staffCount - 1;
    const hours = isLast ? totalHours - hoursUsed : Math.round((shares[i] / shareSum) * totalHours);
    hoursUsed += hours;
    return { name: s.name, rate: s.rate, hours, charges: Math.round(hours * s.rate * 100) / 100, status: 'Chargable' };
  });

  // Rescale charges so they reconcile exactly to totalCharges (rates are
  // illustrative, not meant to literally multiply out to the real total).
  const rawChargeSum = rows.reduce((sum, r) => sum + r.charges, 0);
  if (rawChargeSum > 0) {
    let chargesUsed = 0;
    rows.forEach((r, i) => {
      if (i === rows.length - 1) {
        r.charges = Math.round((totalCharges - chargesUsed) * 100) / 100;
      } else {
        r.charges = Math.round(((r.charges / rawChargeSum) * totalCharges) * 100) / 100;
        chargesUsed += r.charges;
      }
    });
  }

  return rows;
}

sampleEngagementProgressingRows.forEach((row, idx) => {
  const categories = [
    ['pending', row.pendingHours, row.pendingCharges],
    ['approved', row.approvedHours, row.approvedCharges],
    ['booked', row.bookedHours, row.bookedCharges],
  ];
  categories.forEach(([category, hours, charges]) => {
    const key = `${row.id}:${category}`;
    if (!STAFF_DETAIL[key]) {
      STAFF_DETAIL[key] = synthesizeStaffDetail(hours, charges, idx + category.length);
    }
  });
});
